import { type DragEvent, type ReactElement, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarPlus,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import type { BoardCalendar, BoardCalendarEntry } from '@molt/shared';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import {
  addCalendarDays,
  boardCalendarEntryRange,
  buildBoardCalendarGrid,
  calendarDateFromKey,
  calendarDateKey,
  diffCalendarDays,
  placeBoardCalendarEntries,
  timestampDateKey,
  type BoardCalendarMode,
  type BoardCalendarWeek,
  type CalendarWeekStartsOn,
} from '@/lib/board-calendar';
import { cardDisplayTitle } from '@/lib/card-title';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { weekStartForLocale } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import { boardViewLabel } from '@/lib/view-state';

export interface BoardCalendarDayIntent {
  readonly dateKey: string;
}

export interface BoardCalendarCardIntent {
  readonly entry: BoardCalendarEntry;
  readonly cardId: string;
}

export interface BoardCalendarRescheduleIntent {
  readonly entry: Extract<BoardCalendarEntry, { type: 'card' }>;
  readonly cardId: string;
  readonly fromDateKey: string;
  readonly toDateKey: string;
  readonly dayDelta: number;
  readonly preserveDuration: boolean;
}

export interface BoardCalendarResizeIntent {
  readonly entry: Extract<BoardCalendarEntry, { type: 'card' }>;
  readonly cardId: string;
  readonly edge: 'start' | 'due';
  readonly fromDateKey: string;
  readonly toDateKey: string;
}

export interface BoardCalendarViewProps {
  readonly calendar: BoardCalendar;
  readonly label?: string | undefined;
  readonly className?: string | undefined;
  readonly mode?: BoardCalendarMode | undefined;
  readonly anchorDate?: Date | string | undefined;
  readonly weekStartsOn?: CalendarWeekStartsOn | undefined;
  readonly readonly?: boolean | undefined;
  readonly showModeSwitcher?: boolean | undefined;
  readonly onModeChange?: ((mode: BoardCalendarMode) => void) | undefined;
  readonly onOpenCard?: ((intent: BoardCalendarCardIntent) => void) | undefined;
  readonly onCreateCard?: ((intent: BoardCalendarDayIntent) => void) | undefined;
  readonly onReschedule?: ((intent: BoardCalendarRescheduleIntent) => void) | undefined;
  readonly onResize?: ((intent: BoardCalendarResizeIntent) => void) | undefined;
  readonly onOpenExcludedCards?: (() => void) | undefined;
}

const DRAG_MIME = 'application/x-molt-board-calendar-card';

/**
 * ⚠ KEYS, not translated sentences, and the difference is not cosmetic.
 * `refusalKey` below is React state that OUTLIVES the render which set it, so a
 * resolved sentence stored in it would still be on screen in the previous
 * language after the catalogue query resolves underneath — the defect
 * `BoardMapView.tsx:80–84` records and `BoardTableView.tsx`'s `dragRefusalKey`
 * is the worked example. `t` is applied at render, never at `setState`.
 *
 * ⚠ Invisible to the `t('…')` anchor by construction, which is why
 * `board-calendar-timeline.routing.test.ts` holds them by these CONSTANTS
 * appearing in the source plus a rendering assertion.
 */
const REFUSAL_READONLY_KEY = 'boardCalendar.refusal.readonly';
const REFUSAL_DUE_BEFORE_START_KEY = 'boardCalendar.refusal.dueBeforeStart';
const REFUSAL_START_AFTER_DUE_KEY = 'boardCalendar.refusal.startAfterDue';

export function BoardCalendarView({
  calendar,
  label,
  className,
  mode: controlledMode,
  anchorDate,
  weekStartsOn,
  readonly = false,
  showModeSwitcher = true,
  onModeChange,
  onOpenCard,
  onCreateCard,
  onReschedule,
  onResize,
  onOpenExcludedCards,
}: BoardCalendarViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const { language } = useLocalePreferencesValue();
  const [localMode, setLocalMode] = useState<BoardCalendarMode>(
    controlledMode ?? 'month',
  );
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [refusalKey, setRefusalKey] = useState<string | null>(null);
  const mode = controlledMode ?? localMode;
  /**
   * `FR-NOTIF-032` -- the week start is DERIVED from the member's stored
   * language, never stored beside it. An explicit prop still wins, so callers
   * that already thread a value (both `BoardFace` renders) keep deciding.
   */
  const resolvedWeekStartsOn = weekStartsOn ?? weekStartForLocale(language);
  const anchor =
    anchorDate ?? timestampDateKey(calendar.range.from) ?? calendarDateKey(new Date());
  const grid = useMemo(
    () =>
      buildBoardCalendarGrid({
        mode,
        anchorDate: anchor,
        weekStartsOn: resolvedWeekStartsOn,
      }),
    [anchor, mode, resolvedWeekStartsOn],
  );
  const placements = useMemo(
    () => placeBoardCalendarEntries(calendar.entries, grid.days),
    [calendar.entries, grid.days],
  );
  /* ⚠ The FALLBACK, not the only value. `label` was a default parameter
     (`label = 'Calendar view'`) and a default parameter cannot call `t`, which
     is the same wall `ALL_WORKSPACES_LABEL` hit. Composed here rather than
     declared, because `BoardFace.tsx` already names this very
     `id="board-view-content"` region in its error and loading branches with
     this exact composition — one node, one accessible name. The prop stays: the
     Calendar Power-Up dialog passes `t('boardFace.powerUps.calendar')` and must
     keep naming its own region. */
  const regionLabel =
    label ?? t('boardFace.viewRegion', { view: boardViewLabel(t, 'calendar') });

  function changeMode(nextMode: BoardCalendarMode) {
    if (controlledMode === undefined) setLocalMode(nextMode);
    onModeChange?.(nextMode);
  }

  function handleDrop(event: DragEvent<HTMLElement>, dateKey: string) {
    event.preventDefault();
    const cardId = event.dataTransfer.getData(DRAG_MIME) || draggedCardId;
    const entry = calendar.entries.find(
      (candidate) => candidate.type === 'card' && candidate.card.id === cardId,
    );
    if (entry === undefined) return;
    if (readonly) {
      setRefusalKey(REFUSAL_READONLY_KEY);
      return;
    }
    emitReschedule(entry, dateKey);
  }

  function emitReschedule(entry: BoardCalendarEntry, toDateKey: string) {
    if (entry.type !== 'card') return;
    const fromDateKey = timestampDateKey(entry.dueAt);
    if (fromDateKey === null || fromDateKey === toDateKey) return;
    onReschedule?.({
      entry,
      cardId: entry.card.id,
      fromDateKey,
      toDateKey,
      dayDelta: diffCalendarDays(fromDateKey, toDateKey),
      preserveDuration: entry.startAt !== null,
    });
  }

  function emitResize(
    entry: BoardCalendarEntry,
    edge: 'start' | 'due',
    toDateKey: string,
  ) {
    if (entry.type !== 'card') return;
    const range = boardCalendarEntryRange(entry);
    const fromDateKey =
      edge === 'start' ? range?.startKey : timestampDateKey(entry.dueAt);
    if (fromDateKey === undefined || fromDateKey === null || fromDateKey === toDateKey) {
      return;
    }
    if (edge === 'due' && entry.startAt !== null) {
      const startKey = timestampDateKey(entry.startAt);
      if (startKey !== null && toDateKey < startKey) {
        setRefusalKey(REFUSAL_DUE_BEFORE_START_KEY);
        return;
      }
    }
    if (edge === 'start') {
      const dueKey = timestampDateKey(entry.dueAt);
      if (dueKey !== null && toDateKey > dueKey) {
        setRefusalKey(REFUSAL_START_AFTER_DUE_KEY);
        return;
      }
    }
    onResize?.({ entry, cardId: entry.card.id, edge, fromDateKey, toDateKey });
  }

  const monthLabel = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(calendarDateFromKey(grid.days[Math.floor(grid.days.length / 2)]!.key));

  return (
    <section
      aria-label={regionLabel}
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-surface px-3 pb-3 text-ink',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{monthLabel}</h2>
          <p className="text-card text-ink-muted">{calendar.board.title}</p>
        </div>

        {showModeSwitcher ? (
          <div className="flex items-center gap-1 rounded-card border border-border-strong bg-surface-raised p-1">
            <Button
              type="button"
              size="sm"
              variant="toggle"
              pressed={mode === 'month'}
              aria-label={t('boardCalendar.showMonth')}
              onClick={() => changeMode('month')}
            >
              {t('boardCalendar.mode.month')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="toggle"
              pressed={mode === 'week'}
              aria-label={t('boardCalendar.showWeek')}
              onClick={() => changeMode('week')}
            >
              {t('boardCalendar.mode.week')}
            </Button>
          </div>
        ) : null}
      </div>

      {calendar.excludedCardCount > 0 ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-card"
        >
          <span>
            {t(
              'boardCalendar.excluded',
              { count: calendar.excludedCardCount },
              calendar.excludedCardCount,
            )}
          </span>
          {onOpenExcludedCards === undefined ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onOpenExcludedCards}
            >
              {t('boardCalendar.reviewExcluded')}
            </Button>
          )}
        </div>
      ) : null}

      {refusalKey === null ? null : (
        <div role="status" className="sr-only">
          {t(refusalKey)}
        </div>
      )}

      <div
        role="grid"
        aria-label={
          mode === 'month'
            ? t('boardCalendar.gridMonth', { month: monthLabel })
            : t('boardCalendar.gridWeek', { month: monthLabel })
        }
        className="min-h-0 flex-1 overflow-auto rounded-card border border-border-strong bg-panel"
      >
        <div
          role="row"
          className="sticky top-0 z-10 grid grid-cols-7 border-b border-border bg-surface-raised text-xs font-semibold uppercase text-ink-muted"
        >
          {grid.weeks[0]!.days.map((day) => (
            <div key={day.key} role="columnheader" className="px-2 py-2">
              {weekdayLabel(day.date)}
            </div>
          ))}
        </div>

        <div className="grid gap-0">
          {grid.weeks.map((week) => (
            <CalendarWeekRow
              key={week.index}
              week={week}
              entries={placements.filter((placement) =>
                placement.segments.some((segment) => segment.weekIndex === week.index),
              )}
              readonly={readonly}
              onCreateCard={onCreateCard}
              onOpenCard={onOpenCard}
              onDragStart={(entry) => {
                setDraggedCardId(entry.card.id);
                setRefusalKey(null);
              }}
              onDrop={handleDrop}
              onReschedule={onReschedule === undefined ? undefined : emitReschedule}
              onResize={onResize === undefined ? undefined : emitResize}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface CalendarWeekRowProps {
  readonly week: BoardCalendarWeek;
  readonly entries: readonly ReturnType<typeof placeBoardCalendarEntries>[number][];
  readonly readonly: boolean;
  readonly onCreateCard?: ((intent: BoardCalendarDayIntent) => void) | undefined;
  readonly onOpenCard?: ((intent: BoardCalendarCardIntent) => void) | undefined;
  readonly onDragStart: (entry: Extract<BoardCalendarEntry, { type: 'card' }>) => void;
  readonly onDrop: (event: DragEvent<HTMLElement>, dateKey: string) => void;
  readonly onReschedule?:
    ((entry: BoardCalendarEntry, toDateKey: string) => void) | undefined;
  readonly onResize?:
    | ((entry: BoardCalendarEntry, edge: 'start' | 'due', toDateKey: string) => void)
    | undefined;
}

function CalendarWeekRow({
  week,
  entries,
  readonly,
  onCreateCard,
  onOpenCard,
  onDragStart,
  onDrop,
  onReschedule,
  onResize,
}: CalendarWeekRowProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const segments = entries.flatMap((placement) =>
    placement.segments
      .filter((segment) => segment.weekIndex === week.index)
      .map((segment) => ({ entry: placement.entry, segment })),
  );
  const rowMinHeight = calendarWeekRowMinHeight(segments.map(({ entry }) => entry));

  return (
    <div
      role="row"
      className="relative border-b border-border last:border-b-0"
      style={{ minHeight: rowMinHeight }}
    >
      <div className="grid grid-cols-7" style={{ minHeight: rowMinHeight }}>
        {week.days.map((day) => (
          <div
            key={day.key}
            role="gridcell"
            aria-label={longDateLabel(day.key)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => onDrop(event, day.key)}
            className={cn(
              'border-r border-border px-2 py-2 last:border-r-0',
              day.inAnchorMonth ? 'bg-panel' : 'bg-surface text-ink-muted',
              day.isToday ? 'ring-2 ring-inset ring-accent' : null,
            )}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="text-xs font-semibold">{day.date.getDate()}</span>
              {onCreateCard === undefined ? null : (
                <IconButton
                  size="sm"
                  label={t('boardCalendar.createCard', {
                    date: longDateLabel(day.key),
                  })}
                  disabled={readonly}
                  title={readonly ? t('boardCalendar.readonlyReason') : undefined}
                  onClick={() => onCreateCard({ dateKey: day.key })}
                >
                  <CalendarPlus className="size-4" />
                </IconButton>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-9 grid grid-cols-7 gap-y-1 px-2 pb-2">
        {segments.map(({ entry, segment }) => (
          <CalendarEntrySegment
            key={`${entryKey(entry)}-${segment.startKey}`}
            entry={entry}
            startDayIndex={segment.startDayIndex}
            span={segment.span}
            readonly={readonly}
            onOpenCard={onOpenCard}
            onDragStart={onDragStart}
            onReschedule={onReschedule}
            onResize={onResize}
          />
        ))}
      </div>
    </div>
  );
}

function calendarWeekRowMinHeight(entries: readonly BoardCalendarEntry[]): number {
  const entryHeight = entries.reduce((height, entry) => {
    if (entry.type === 'checklist_item') return height + 48;
    return height + (entry.startAt === null ? 40 : 68);
  }, 0);
  const entryGaps = Math.max(entries.length - 1, 0) * 4;
  return Math.max(80, 44 + entryHeight + entryGaps);
}

interface CalendarEntrySegmentProps {
  readonly entry: BoardCalendarEntry;
  readonly startDayIndex: number;
  readonly span: number;
  readonly readonly: boolean;
  readonly onOpenCard?: ((intent: BoardCalendarCardIntent) => void) | undefined;
  readonly onDragStart: (entry: Extract<BoardCalendarEntry, { type: 'card' }>) => void;
  readonly onReschedule?:
    ((entry: BoardCalendarEntry, toDateKey: string) => void) | undefined;
  readonly onResize?:
    | ((entry: BoardCalendarEntry, edge: 'start' | 'due', toDateKey: string) => void)
    | undefined;
}

function CalendarEntrySegment({
  entry,
  startDayIndex,
  span,
  readonly,
  onOpenCard,
  onDragStart,
  onReschedule,
  onResize,
}: CalendarEntrySegmentProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title = entryTitle(t, entry);
  const dueKey = timestampDateKey(entry.dueAt);
  const startKey =
    entry.type === 'card' && entry.startAt !== null
      ? timestampDateKey(entry.startAt)
      : null;
  const isCard = entry.type === 'card';
  const canReschedule = isCard && onReschedule !== undefined;
  const canResize = isCard && onResize !== undefined;
  const parentTitle = cardDisplayTitle(t, entry.card.title);
  const dueLabel =
    dueKey === null ? t('boardCalendar.unknownDate') : longDateLabel(dueKey);
  /* ⚠ Resolved ONCE and spliced into `{reason}` below, while every `{action}`
     stays an inline `t('…')`. The tempting further tidy — one
     `controlLabel(t, key, values, readonly)` helper — is refused on purpose: it
     would hide all twelve of this file's control names from the `t('…')`
     anchor in a single edit, with the pin still green. */
  const readonlyReason = t('boardCalendar.readonlyReason');

  return (
    <div
      className={cn(
        'pointer-events-auto min-w-0 rounded-card border border-border-strong bg-surface-raised p-1 shadow-card',
        entry.type === 'checklist_item' ? 'border-accent/60' : null,
      )}
      style={{ gridColumn: `${startDayIndex + 1} / span ${span}` }}
    >
      <div className="flex min-w-0 items-start gap-1">
        <button
          type="button"
          draggable={canReschedule}
          aria-label={
            entry.type === 'checklist_item'
              ? t('boardCalendar.openChecklistItem', {
                  item: title,
                  card: parentTitle,
                  date: dueLabel,
                })
              : t('boardCalendar.openCard', { title, date: dueLabel })
          }
          onDragStart={(event) => {
            if (!canReschedule || entry.type !== 'card') return;
            event.dataTransfer.effectAllowed = readonly ? 'none' : 'move';
            event.dataTransfer.setData(DRAG_MIME, entry.card.id);
            onDragStart(entry);
          }}
          onClick={() => {
            onOpenCard?.({ entry, cardId: entry.card.id });
          }}
          className={cn(
            'min-w-0 flex-1 truncate rounded-card px-2 py-1 text-left text-xs font-medium text-ink hover:bg-panel',
            entry.completed ? 'line-through opacity-70' : null,
          )}
        >
          {title}
          {entry.type === 'checklist_item' ? (
            <span className="mt-0.5 block truncate text-[0.6875rem] font-normal text-ink-muted">
              {t('boardCalendar.checklistItemParent', { card: parentTitle })}
            </span>
          ) : null}
        </button>

        {canReschedule ? (
          <div className="flex shrink-0 items-center gap-0.5">
            <IconButton
              size="sm"
              label={
                readonly
                  ? t('boardCalendar.controlUnavailable', {
                      action: t('boardCalendar.moveEarlier', { title }),
                      reason: readonlyReason,
                    })
                  : t('boardCalendar.moveEarlier', { title })
              }
              disabled={readonly}
              title={readonly ? readonlyReason : undefined}
              onClick={() =>
                dueKey !== null && onReschedule(entry, previousDateKey(dueKey))
              }
            >
              <ArrowLeft className="size-3.5" />
            </IconButton>
            <IconButton
              size="sm"
              label={
                readonly
                  ? t('boardCalendar.controlUnavailable', {
                      action: t('boardCalendar.moveLater', { title }),
                      reason: readonlyReason,
                    })
                  : t('boardCalendar.moveLater', { title })
              }
              disabled={readonly}
              title={readonly ? readonlyReason : undefined}
              onClick={() => dueKey !== null && onReschedule(entry, nextDateKey(dueKey))}
            >
              <ArrowRight className="size-3.5" />
            </IconButton>
          </div>
        ) : null}
      </div>

      {canResize && startKey !== null && dueKey !== null ? (
        <div className="mt-1 flex items-center justify-end gap-0.5">
          <IconButton
            size="sm"
            label={
              readonly
                ? t('boardCalendar.controlUnavailable', {
                    action: t('boardCalendar.moveStartEarlier', { title }),
                    reason: readonlyReason,
                  })
                : t('boardCalendar.moveStartEarlier', { title })
            }
            disabled={readonly}
            title={readonly ? readonlyReason : undefined}
            onClick={() => onResize(entry, 'start', previousDateKey(startKey))}
          >
            <ChevronsLeft className="size-3.5" />
          </IconButton>
          <IconButton
            size="sm"
            label={
              readonly
                ? t('boardCalendar.controlUnavailable', {
                    action: t('boardCalendar.moveStartLater', { title }),
                    reason: readonlyReason,
                  })
                : t('boardCalendar.moveStartLater', { title })
            }
            disabled={readonly}
            title={readonly ? readonlyReason : undefined}
            onClick={() => onResize(entry, 'start', nextDateKey(startKey))}
          >
            <ChevronsRight className="size-3.5" />
          </IconButton>
          <IconButton
            size="sm"
            label={
              readonly
                ? t('boardCalendar.controlUnavailable', {
                    action: t('boardCalendar.moveDueEarlier', { title }),
                    reason: readonlyReason,
                  })
                : t('boardCalendar.moveDueEarlier', { title })
            }
            disabled={readonly}
            title={readonly ? readonlyReason : undefined}
            onClick={() => onResize(entry, 'due', previousDateKey(dueKey))}
          >
            <ChevronsLeft className="size-3.5" />
          </IconButton>
          <IconButton
            size="sm"
            label={
              readonly
                ? t('boardCalendar.controlUnavailable', {
                    action: t('boardCalendar.moveDueLater', { title }),
                    reason: readonlyReason,
                  })
                : t('boardCalendar.moveDueLater', { title })
            }
            disabled={readonly}
            title={readonly ? readonlyReason : undefined}
            onClick={() => onResize(entry, 'due', nextDateKey(dueKey))}
          >
            <ChevronsRight className="size-3.5" />
          </IconButton>
        </div>
      ) : null}
    </div>
  );
}

/**
 * ⚠ `t` FIRST and REQUIRED, the idiom `lib/card-title.ts:19–23` states: a
 * defaulted translator lets one call site fall through to a default that looks
 * correct, and a required one makes `tsc` enumerate the callers.
 *
 * ⚠ The two arms are NOT symmetrical and that is the ruling. A checklist item
 * has no shared fallback helper, so `boardCalendar.untitledChecklistItem` is
 * declared; a card has one, so this REUSES `cardDisplayTitle` rather than
 * becoming a seventh author of the card fallback. ⚠ Behaviour change, intended
 * and documented at `card-title.ts:25–32`: the old `title.trim() || …` also
 * STRIPPED surrounding whitespace from a non-blank title, so a card called
 * `'  Ship it  '` was announced differently from how it was drawn.
 */
function entryTitle(
  t: InterfaceLanguageContextValue['t'],
  entry: BoardCalendarEntry,
): string {
  if (entry.type === 'checklist_item') {
    return entry.checklistItem.text.trim() === ''
      ? t('boardCalendar.untitledChecklistItem')
      : entry.checklistItem.text;
  }
  return cardDisplayTitle(t, entry.card.title);
}

function entryKey(entry: BoardCalendarEntry): string {
  return entry.type === 'checklist_item' ? entry.checklistItem.id : entry.card.id;
}

/**
 * ⚠ NOT ROUTED, and REPORTED rather than fixed. `undefined` is the BROWSER's
 * locale, which ignores the user's locale preferences entirely — the class of
 * `BoardFace.tsx:935`'s `calendarDateLabel` and `BoardTableView`'s
 * `dueDateLabel`. What settles it is measured: `lib/locale-formatting.ts` holds
 * no formatter with this option set, so adopting one is not a substitution — it
 * changes rendered bytes that fourteen `FR-VIEW-*` specs bind. Behaviour work,
 * not catalogue routing.
 */
function weekdayLabel(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date);
}

function longDateLabel(dateKey: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(calendarDateFromKey(dateKey));
}

function nextDateKey(dateKey: string): string {
  return calendarDateKey(addCalendarDays(calendarDateFromKey(dateKey), 1));
}

function previousDateKey(dateKey: string): string {
  return calendarDateKey(addCalendarDays(calendarDateFromKey(dateKey), -1));
}
